// Session 27: renders one lesson from its content object.
//
// The nine-part template is guaranteed here, not in each lesson file:
//   1 Why it matters      from lesson.why
//   2 The concept         concept blocks
//   3 Try it              tabs blocks (only where the clicks differ)
//   4 Your turn           exercise blocks
//   5 Quick check         quickcheck block
//   6 Prompt card         promptcard block
//   7 Takeaway            takeaway block
//   8 What's missing?     appended by this renderer, on every lesson
//   9 Video slot          appended by this renderer, hidden from learners
//
// So a lesson file lists only what is specific to it, and no lesson can
// accidentally ship without its feedback box.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../supabase.js';
import { SaveStatus, useSaveStatus } from '../SaveStatus.jsx';
import {
  ConceptBlock, TabsBlock, ExerciseBlock, QuickCheckBlock,
  PromptCardBlock, TakeawayBlock, WhatsMissingBlock, VideoSlot,
} from './blocks.jsx';

export default function LessonRenderer({
  course, lesson, readOnly, isAgency, isComplete, onCompleted, clientQuery,
}) {
  const [exerciseDone, setExerciseDone] = useState({});
  const [done, setDone] = useState(!!isComplete);
  const save = useSaveStatus();

  useEffect(() => { setDone(!!isComplete); }, [isComplete, lesson.key]);
  // A different lesson means a different set of ticks.
  useEffect(() => { setExerciseDone({}); }, [lesson.key]);

  // Mark the lesson started once, so the agency view can tell "opened it" from
  // "finished it". Preview never writes.
  useEffect(() => {
    if (readOnly) return;
    let cancelled = false;
    (async () => {
      const { error } = await supabase.rpc('portal_course_start_lesson', {
        p_course_key: course.key, p_lesson_key: lesson.key,
      });
      // A failed start is not worth interrupting the reader for: the lesson
      // still reads, and completing it writes the row anyway.
      if (error && !cancelled) console.warn('[course] start_lesson:', error.message);
    })();
    return () => { cancelled = true; };
  }, [course.key, lesson.key, readOnly]);

  const toggleStep = useCallback(async (id, checked) => {
    setExerciseDone((d) => ({ ...d, [id]: checked }));
    if (readOnly) return;
    const { error } = await supabase.rpc('portal_course_save_response', {
      p_course_key: course.key,
      p_lesson_key: lesson.key,
      p_kind: 'exercise_done',
      // Session 27b: one row per step, not one per lesson.
      p_slot: id,
      p_payload: { step: id, done: checked },
    });
    if (error) console.warn('[course] exercise_done:', error.message);
  }, [course.key, lesson.key, readOnly]);

  async function complete() {
    if (readOnly) return;
    save.start();
    const { error } = await supabase.rpc('portal_course_complete_lesson', {
      p_course_key: course.key, p_lesson_key: lesson.key,
    });
    if (error) return save.fail(error.message);
    setDone(true);
    save.ok();
    if (onCompleted) onCompleted();
  }

  const next = nextReady(course, lesson.key);

  return (
    <article className="course-lesson">
      <p className="course-crumb">
        <Link to={`/courses/${course.key}${clientQuery}`}>{course.title}</Link>
      </p>
      <h1>{lesson.title}</h1>
      <p className="lede">{lesson.minutes} minutes{lesson.outcome ? ` · ${lesson.outcome}` : ''}</p>

      {lesson.why && (
        <section className="course-block course-why">
          <h2>Why it matters</h2>
          <p>{lesson.why}</p>
        </section>
      )}

      {lesson.blocks.map((block, i) => {
        const key = `${lesson.key}-${i}`;
        if (block.type === 'concept')    return <ConceptBlock    key={key} block={block} />;
        if (block.type === 'tabs')       return <TabsBlock       key={key} block={block} isAgency={isAgency} />;
        if (block.type === 'takeaway')   return <TakeawayBlock   key={key} block={block} />;
        if (block.type === 'exercise') {
          return (
            <ExerciseBlock
              key={key} block={block} courseKey={course.key} lessonKey={lesson.key}
              done={exerciseDone} onToggle={toggleStep} readOnly={readOnly}
            />
          );
        }
        if (block.type === 'quickcheck') {
          return (
            <QuickCheckBlock
              key={key} block={block} courseKey={course.key}
              lessonKey={lesson.key} readOnly={readOnly}
            />
          );
        }
        if (block.type === 'promptcard') {
          return (
            <PromptCardBlock
              key={key} block={block} courseKey={course.key}
              lessonKey={lesson.key} readOnly={readOnly}
            />
          );
        }
        // An unknown block type is a content bug, not a reason to blank the
        // page. The agency sees it; a learner sees nothing.
        if (isAgency) {
          return <p key={key} className="course-callout course-callout-verify">
            Agency note: unknown lesson block type &ldquo;{block.type}&rdquo;.
          </p>;
        }
        return null;
      })}

      {/* Session 27b: keyed on the lesson. React Router reuses this element
          across a param change, so without the key an unsent draft written
          about one lesson stayed in the box and could be filed against the
          next one. */}
      <WhatsMissingBlock
        key={lesson.key} courseKey={course.key} lessonKey={lesson.key} readOnly={readOnly}
      />
      <VideoSlot note={lesson.videoNote} isAgency={isAgency} />

      <div className="course-finish">
        {readOnly ? (
          <p className="course-muted">
            This is the free sample lesson. The rest of the course opens once it is part of your plan.
          </p>
        ) : (
          <>
            <button type="button" className="btn btn-primary" onClick={complete} disabled={done}>
              {done ? 'Finished' : 'Mark this lesson finished'}
            </button>
            <SaveStatus status={save.status} />
            {done && next && (
              <Link className="btn" to={`/courses/${course.key}/${next.key}${clientQuery}`}>
                Next: {next.title}
              </Link>
            )}
            {done && !next && (
              <Link className="btn" to={`/courses/${course.key}${clientQuery}`}>
                Back to the course
              </Link>
            )}
          </>
        )}
      </div>
    </article>
  );
}

function nextReady(course, lessonKey) {
  const i = course.lessons.findIndex((l) => l.key === lessonKey);
  if (i < 0) return null;
  return course.lessons.slice(i + 1).find((l) => l.status === 'ready') || null;
}
